export function formatCallDuration(totalSeconds) {
  const seconds = Math.max(0, Number(totalSeconds) || 0);
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins <= 0) return `${secs} sec`;
  if (secs === 0) return `${mins} min`;
  return `${mins} min ${secs} sec`;
}

/** Client calls can run up to ~1 hour; keep "In progress" for 90 minutes without end metadata. */
const STALE_OPEN_CALL_MS = 90 * 60 * 1000;

/** True only for recent calls missing end metadata (avoids stale "In progress" UI). */
export function isCallInProgress(callLog) {
  if (!callLog || callLog.durationSeconds != null || callLog.endedAt) return false;
  const startedMs = new Date(callLog.startedAt || 0).getTime();
  if (!Number.isFinite(startedMs)) return false;
  return Date.now() - startedMs < STALE_OPEN_CALL_MS;
}

export function getCallDurationLabel(callLog) {
  if (isCallInProgress(callLog)) return 'In progress';
  if (callLog?.durationSeconds != null) return formatCallDuration(callLog.durationSeconds);
  return 'Ended';
}

export function getCallDirectionLabel(direction) {
  return direction === 'inbound' ? 'Inbound call' : 'Outbound call';
}

/** Missed, unanswered, or otherwise zero-duration completed calls. */
export function isUnsuccessfulCall(callLog) {
  if (!callLog) return false;
  if (callLog.durationSeconds == null && !callLog.endedAt) return false;
  return Number(callLog.durationSeconds) === 0;
}

const UNANSWERED_CALL_OUTCOMES = new Set(['no-answer', 'busy', 'failed', 'canceled', 'cancelled']);

/** Inbound call the team never answered (voicemail counts as missed). */
export function isMissedInboundCall(callLog) {
  if (!callLog || callLog.direction !== 'inbound') return false;
  if (callLog.missedCall === true || callLog.hasVoicemail === true) return true;
  const outcome = String(callLog.callOutcome || '').trim().toLowerCase();
  if (outcome === 'voicemail' || UNANSWERED_CALL_OUTCOMES.has(outcome)) return true;
  return isUnsuccessfulCall(callLog);
}

export function isUnreadMissedCall(callLog, userEmail) {
  if (!userEmail || !isMissedInboundCall(callLog)) return false;
  return !(Array.isArray(callLog.readBy) && callLog.readBy.includes(userEmail));
}

export function countUnreadMissedCalls(callLogs, userEmail) {
  if (!userEmail || !Array.isArray(callLogs)) return 0;
  return callLogs.filter((log) => isUnreadMissedCall(log, userEmail)).length;
}

export function buildCallTimelineItem(callLog) {
  return {
    id: callLog.id,
    kind: 'call',
    direction: callLog.direction || 'outbound',
    startedAt: callLog.startedAt,
    createdAt: callLog.startedAt,
    endedAt: callLog.endedAt || null,
    durationSeconds: callLog.durationSeconds,
    adviserName: callLog.adviserName,
    adviserEmail: callLog.adviserEmail,
    teamSmsNumber: callLog.teamSmsNumber,
    clientId: callLog.clientId,
    hasRecording: Boolean(callLog.hasRecording),
    hasVoicemail: Boolean(callLog.hasVoicemail),
    hasTranscript: Boolean(callLog.hasTranscript),
    transcriptStatus: callLog.transcriptStatus || null,
  };
}

export function buildSmsTimelineItem(message) {
  return {
    ...message,
    kind: 'sms',
    createdAt: message.createdAt,
  };
}

export function mergeCommunicationsTimeline(smsMessages = [], callLogs = []) {
  const items = [
    ...smsMessages.map(buildSmsTimelineItem),
    ...callLogs.map(buildCallTimelineItem),
  ];
  return items.sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
}
